import { invalidateTypedQueries, useApiClient } from '@/hooks/useApiClient'
import type { components } from '@/types/api/v1'
import { CalendarFilled, CalendarOutlined, ExclamationCircleFilled, MoonFilled, SunFilled } from '@ant-design/icons'
import { GraviButton, Texto } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import type { ICellRendererParams } from 'ag-grid-community'
import { type ButtonProps, Flex, Popover, Spin } from 'antd'
import type { Dayjs } from 'dayjs'
import { type PropsWithChildren, memo, useEffect, useMemo, useState } from 'react'
import { AmIcon, PmIcon } from '../../../Manage/TemplateManagement/ShiftTiles/ShiftIcons'
import { useSchedulePageFilters } from '../../Context/Filters/SchedulePageFilterContext'
import { useScheduleItems } from '../../Context/ScheduleItems/ScheduleItemsContext'
import { type ScheduleItemWithConflicts, getDayKey } from '../../Context/ScheduleItems/scheduleItemStore'
import { TractorIcon, TrailerIcon } from '../../Icons/Icons'
import { formToScheduleItem } from '../../utils/formToScheduleItem'
import { colDefIdToDay, getDriverShift } from '../../utils/getDefaultShift'
import { DriverCellPopover } from '../DriverCellPopover'
import { NewEventDropdown } from './NewEventDropdown'
import { ShiftBlock } from './ShiftBlock'

const ShiftTime = ({ value, error, showZone }: { value: Dayjs; error?: boolean; showZone?: boolean }) => (
  <>
    <Texto weight='bolder' appearance={error ? 'error' : 'default'}>
      {value.format('h:mm')}
    </Texto>
    <Texto weight='600' category='label' appearance={error ? 'error' : 'hint'}>
      {value.format(!showZone ? 'A' : 'A (z)')}
    </Texto>
  </>
)

const ShiftRenderer = ({
  value,
  isLoading,
  errorData,
  ...rest
}: { errorData?: Array<{ value: string }>; value: ScheduleItemWithConflicts; isLoading?: boolean } & Omit<
  ButtonProps,
  'value'
>) => {
  const { datePickerType } = useSchedulePageFilters()

  const hasError = errorData || value.conflictingItems.length || value.errors.length
  const shiftType = hasError ? 'error' : value.shift

  //would likely be cleaner to measure cell, but less performant so we'll go with the ez route for now
  //todo: the line height on this element is weird; investigate
  const cellContent =
    datePickerType === 'week' ? (
      <>
        {value.shift !== 'am' && value.shift !== 'pm' && <Texto appearance='medium'>{value.shift}</Texto>}
        <div
          style={{
            display: 'flex',
            flex: '1 0 80%',
            flexWrap: 'nowrap',
            overflow: 'hidden',
            width: '100%',
            gap: '2px',
            alignItems: 'center',
          }}
        >
          <ShiftTime value={value.start} error={shiftType === 'error'} />
          <Texto weight='bolder'>-</Texto>
          <ShiftTime value={value.end} error={shiftType === 'error'} showZone />
          {isLoading && <Spin size='small' />}
        </div>
        {value.tractor_number && (
          <div style={{ display: 'flex', flex: 1, gap: '2px', alignItems: 'center' }}>
            <TractorIcon />
            <Texto weight='bold'>{value.tractor_number ?? 'N/A'}</Texto>
          </div>
        )}
        {value.trailer_number && (
          <div style={{ display: 'flex', flex: 1, gap: '2px', alignItems: 'center' }}>
            <TrailerIcon />
            <Texto weight='bold'>{value.trailer_number ?? 'N/A'}</Texto>
          </div>
        )}
        {!!hasError && (
          <Popover
            title={errorData ? 'Error creating/updating schedule item!' : 'Schedule Conflicts Detected!'}
            content={
              <Flex vertical>
                {(errorData ?? value.errors).map((err) => (
                  <Texto key={err.value}>{err.value}</Texto>
                ))}
              </Flex>
            }
          >
            <ExclamationCircleFilled style={{ marginLeft: 'auto' }} />
          </Popover>
        )}
      </>
    ) : null

  return (
    <ShiftBlock {...rest} shiftType={shiftType}>
      {value.shift === 'am' ? <SunFilled /> : value.shift === 'pm' ? <MoonFilled /> : <CalendarOutlined />}
      {cellContent}
    </ShiftBlock>
  )
}

const MultipleShiftRenderer = ({
  value,
  ...rest
}: { value: ScheduleItemWithConflicts[] } & Omit<ButtonProps, 'value'>) => {
  const { datePickerType } = useSchedulePageFilters()

  const hasError = value.some((v) => v.conflictingItems.length || v.errors.length)

  const shiftInfo = useMemo(() => {
    const shifts = {
      am: { count: 0, color: 'yellow' },
      pm: { count: 0, color: 'blue' },
      other: { count: 0, color: 'gray' },
    }
    let hasError = false
    value.forEach((v) => {
      const key = v.shift === 'am' ? 'am' : v.shift === 'pm' ? 'pm' : 'other'
      shifts[key].count += 1
      if (v.errors.length) {
        shifts[key].color = 'red'
        hasError = true
      }
    })
    return { shifts, hasError }
  }, [value])

  //would likely be cleaner to measure cell, but less performant so we'll go with the ez route for now
  //todo: the line height on this element is weird; investigate
  const cellContent =
    datePickerType === 'week' ? (
      <div
        style={{
          display: 'flex',
          flex: '1 0 80%',
          flexWrap: 'nowrap',
          overflow: 'hidden',
          width: '100%',
          gap: '2px',
          alignItems: 'center',
        }}
      >
        {!!shiftInfo.shifts.am.count && <AmIcon color={shiftInfo.shifts.am.color} />}
        {!!shiftInfo.shifts.pm.count && <PmIcon color={shiftInfo.shifts.pm.color} />}
        {!!shiftInfo.shifts.other.count && <CalendarFilled color={shiftInfo.shifts.other.color} />}
        <Texto weight='bolder' className='mx-2'>{`${value.length} Events`}</Texto>
        {!!hasError && (
          <Popover
            title='Schedule Conflicts Detected!'
            content={
              <Flex vertical>
                {value
                  .flatMap((x) => x.errors)
                  .map((err) => (
                    <Texto key={err.value}>{err.value}</Texto>
                  ))}
              </Flex>
            }
          >
            <ExclamationCircleFilled style={{ marginLeft: 'auto' }} />
          </Popover>
        )}
      </div>
    ) : (
      <Texto>{value.length}</Texto>
    )

  return (
    <ShiftBlock {...rest} shiftType={shiftInfo.hasError ? 'error' : null}>
      {cellContent}
    </ShiftBlock>
  )
}

const MultiShiftItem = ({
  value,
  data,
  day,
}: { value: ScheduleItemWithConflicts[]; data: components['schemas']['DriverInfoRow']; day?: string }) => {
  return (
    <Popover
      trigger='click'
      styles={{
        body: { width: '300px' },
      }}
      content={
        <Flex
          vertical
          className='schedule-grid'
          style={{ borderLeft: 'none', borderRight: 'none', borderBottom: 'none' }}
          gap='4px'
        >
          {value.map((v) => (
            <ShiftItem key={v.event_id} value={v} data={data} day={day} />
          ))}
          <ShiftItem
            hideAmPm={value.some((v) => v.shift === 'am' || v.shift === 'pm')}
            key='new-shift'
            data={data}
            day={day}
          />
        </Flex>
      }
    >
      <MultipleShiftRenderer value={value} />
    </Popover>
  )
}

//for now, we can only create events on an empty cell. If we need to create new schedule events
//on a cell that already has events, the meat of this component would be broken into something reusable
function EmptyCellRenderer({ children }: PropsWithChildren) {
  return <div className='shift-cell no-shift'>{children}</div>
}

const ShiftItem = ({
  value: _value,
  data,
  day,
  canAddNew,
  hideAmPm,
}: {
  value?: ScheduleItemWithConflicts
  data: components['schemas']['DriverInfoRow']
  day?: string
  canAddNew?: boolean
  hideAmPm?: boolean
}) => {
  const [shiftType, setShiftType] = useState<string>()
  const [open, setIsOpen] = useState(false)
  const api = useApiClient()
  const queryClient = useQueryClient()
  const {
    mutateAsync: upsert,
    data: mutationResult,
    submittedAt,
    reset,
    isPending,
    isError,
    error,
    variables,
  } = api.useMutation('post', '/driver/create_event', {
    onMutate: () => {
      setShiftType(undefined)
      setIsOpen(false)
    },
    onSettled: () => {
      invalidateTypedQueries(queryClient, 'post', '/driver/schedule/overview')
    },
  })

  const [valueChangedAt, setValueChangedAt] = useState(new Date().valueOf())
  // biome-ignore lint/correctness/useExhaustiveDependencies: specific side-effect of value changing
  useEffect(() => {
    setValueChangedAt(new Date().valueOf())
  }, [_value])

  const value = useMemo(
    () =>
      variables?.body && submittedAt > valueChangedAt
        ? {
            ...formToScheduleItem(variables.body, getDriverShift(data)?.timezone_data?.depot as string),
            event_id: mutationResult?.id ?? '',
            driver_name: data.name,
          }
        : _value,
    [_value, variables, mutationResult, data, submittedAt, valueChangedAt]
  )
  const errorData = useMemo(() => {
    if (!isError) return
    return error?.detail?.map?.((x) => ({ value: x.msg })) ?? [{ value: 'An unknown error has occured' }]
  }, [isError, error])
  return (
    <DriverCellPopover
      open={!!shiftType || open}
      onOpenChange={(val) => {
        if (!val) setShiftType(undefined)
        setIsOpen(val)
      }}
      onDelete={reset}
      extraButtons={
        !shiftType && canAddNew && !!value?.event_id ? (
          <NewEventDropdown hideAmPm isCarrier={!!data.carrier_id} onSelect={setShiftType}>
            <GraviButton>Add Another Event</GraviButton>
          </NewEventDropdown>
        ) : null
      }
      value={shiftType ? undefined : value}
      day={day}
      type={shiftType}
      data={data}
      onSubmit={(body) => upsert({ body })}
    >
      {value ? (
        <ShiftRenderer errorData={errorData} value={value} isLoading={isPending} />
      ) : (
        <EmptyCellRenderer>
          <NewEventDropdown
            hideAmPm={hideAmPm}
            onSelect={setShiftType}
            isCarrier={!!data.carrier_id}
            shiftPreference={data.shift_preference}
          />
        </EmptyCellRenderer>
      )}
    </DriverCellPopover>
  )
}

function _DayCellRenderer(props: ICellRendererParams<components['schemas']['DriverInfoRow']>) {
  const { search } = useScheduleItems()
  const value = useMemo(
    () =>
      search(
        { index: 'driver', value: props.data?.id ?? '' },
        { index: 'day', value: getDayKey(colDefIdToDay(props?.colDef?.colId)) }
      ),
    [search, props.data?.id, props.colDef?.colId]
  )
  if (!props.data) return null

  if (value.length > 1) {
    return <MultiShiftItem value={value} data={props.data} day={props.colDef?.colId} />
  }

  return <ShiftItem value={value?.[0]} canAddNew data={props.data} day={props.colDef?.colId} />
}
export const DayCellRenderer = memo(_DayCellRenderer)
